import { useEffect, useState } from 'react';
import { api } from '../../api/client.js';
import { courriersApi } from '../../api/resources';
import { tr, trf } from '../../lib/i18n';

/** « 2026-10-16 » s'écrit « 16.10.2026 ». */
const jour = (iso) =>
  String(iso || '')
    .split('-')
    .reverse()
    .join('.');

/**
 * Courriers et décharges dans le classeur : « si un jour j'ai envie d'aller
 * trouver ce document, je le trouve ». Chacun s'ouvre en PDF officiel, celui
 * que le serveur garde.
 *
 * @param {object} props propriétés du composant
 * @param {Function} props.card tuile du classeur (icône, nom, éléments, couleur, rendu)
 * @param {Function} props.piece ligne d'une pièce (clé, texte, droite, couleur, chemin)
 * @param {object} props.styleGroupe style du titre de groupe
 * @param {object} props.couleurs couleurs des deux tuiles
 * @returns {JSX.Element} le groupe du classeur
 */
export function ClasseurLettres({ card, piece, styleGroupe, couleurs }) {
  const [courriers, setCourriers] = useState([]);
  const [decharges, setDecharges] = useState([]);

  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(() => courriersApi.liste())
      .then((l) => vivant && setCourriers(Array.isArray(l) ? l : []))
      .catch(() => {});
    Promise.resolve()
      .then(() => api.get('/decharges'))
      .then((l) => vivant && setDecharges(Array.isArray(l) ? l : []))
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, []);

  /**
   * Supprime un courrier raté ou de trop, après une question : la lettre et
   * son PDF quittent le classeur.
   *
   * @param {object} c courrier du classeur
   */
  const supprime = (c) => {
    if (!window.confirm(trf('Supprimer le courrier n° {0} ?', c.numero || '—'))) return;
    Promise.resolve()
      .then(() => courriersApi.supprime(c.id))
      .then(() => setCourriers((l) => l.filter((x) => x.id !== c.id)))
      .catch((e) => window.alert(e?.message || tr('Le courrier n’a pas pu être supprimé.')));
  };

  return (
    <>
      <div style={styleGroupe}>✉️ Courriers & décharges</div>
      {card('✉️', 'Courriers', courriers, couleurs.courrier, (c, i) => (
        <div key={'courrier-' + i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {piece(
              'courrier-' + i,
              <span translate="no">
                {[jour(c.dateDocument), c.destinataireNom, c.objet].filter(Boolean).join(' · ')}
              </span>,
              c.numero ? 'N° ' + c.numero : '',
              couleurs.courrier,
              `/courriers/${c.id}/document`,
            )}
          </div>
          <button
            type="button"
            aria-label={tr('Supprimer le courrier')}
            onClick={() => supprime(c)}
            style={{
              background: 'none',
              border: 'none',
              color: '#94A3B8',
              cursor: 'pointer',
              fontSize: 13,
              padding: '4px 2px',
            }}
          >
            ✕
          </button>
        </div>
      ))}
      {card('🛡', 'Décharges', decharges, couleurs.decharge, (d, i) =>
        piece(
          'decharge-' + i,
          <span translate="no">
            {[jour(d.dateDocument), d.destinataireNom, d.objet].filter(Boolean).join(' · ')}
          </span>,
          d.numero ? 'N° ' + d.numero : '',
          couleurs.decharge,
          `/decharges/${d.id}/document`,
        ),
      )}
    </>
  );
}
